import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useHasRole, useHostAuth } from '@iqd/shared-app';
import { environment } from '../../../environments/environment';

/** Where the redirect comes back to, and how the screen knows it worked. */
export const MICROSOFT_LINKED_PARAM = 'microsoft';
export const MICROSOFT_LINKED_VALUE = 'linked';

const RETURN_TO = `/user-settings?${MICROSOFT_LINKED_PARAM}=${MICROSOFT_LINKED_VALUE}`;

export interface MicrosoftLinking {
  /** Whether a Microsoft identity is already on this account. */
  linked: boolean;
  onLink: () => Promise<void>;
}

/**
 * Linking a Microsoft 365 account to the one already signed in.
 *
 * Auth0 runs this through the Client Initiated Account Linking Action rather
 * than the old account-link extension rule. The rule linked on its own at
 * every login, which cost a Management API lookup on every silent auth — and
 * this app silently re-authenticates on every page load, so the whole product
 * waited on it. Nothing happens here unless someone asks for it.
 *
 * The Action reads three parameters: `link_account` in the scope to say what
 * is being asked for, an `id_token_hint` proving who is asking, and the
 * connection to link. It merges the Microsoft account into this one, which
 * cannot be undone from the app — the screen says so before starting.
 *
 * Returns undefined where the option does not apply, which is what keeps the
 * row off the screen: no connection configured, or not a member of staff.
 */
export function useMicrosoftLinking(): MicrosoftLinking | undefined {
  const { getIdTokenClaims, loginWithRedirect } = useAuth0();
  const { user } = useHostAuth();
  const connection = environment.auth0.microsoftConnection;
  const internal = useHasRole('internal-user');

  const onLink = useCallback(async () => {
    const claims = await getIdTokenClaims();
    // The Action refuses a request it cannot attribute, and failing here says
    // so while the person is still on the screen rather than after a redirect.
    if (!claims?.__raw) {
      throw new Error('Your session could not be verified. Sign in again and retry.');
    }

    await loginWithRedirect({
      appState: { returnTo: RETURN_TO },
      authorizationParams: {
        // `openid` is added by the SDK regardless. The token that comes back
        // is cached under this scope rather than the one every screen asks
        // for, so ordinary API calls are unaffected by it.
        scope: 'link_account',
        id_token_hint: claims.__raw,
        requested_connection: connection,
      },
    });
  }, [connection, getIdTokenClaims, loginWithRedirect]);

  if (!connection || !internal) return undefined;

  return {
    // Reads the `identities` claim, so a tenant whose Action does not set it
    // reports nothing linked and the screen keeps offering to link. Asking
    // twice costs nothing: the Action stops when the two accounts are already
    // one.
    linked: user?.linkedConnections?.includes(connection) ?? false,
    onLink,
  };
}
